import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  TuiAppearance,
  TuiLabel,
  TuiLoader,
  TuiNotification,
  TuiTextfield,
  TuiTitle,
} from '@taiga-ui/core';
import { TuiChevron, TuiDataListWrapper, TuiSelect, TuiToastService } from '@taiga-ui/kit';
import { TuiCardMedium } from '@taiga-ui/layout';
import { TrainingLevelLimitsStore } from '@domains/athlete/training-level-limits.store';
import { TrainingLevelStore } from '@domains/athlete/training-level.store';
import { ITrainingLevelLimit } from '@domains/athlete/training-level-limits.model';
import { ISportOption, SportConfigurationStore } from '@domains/feature-configuration/sport-configuration.store';
import {
  AXIS_UNIT_DEFS,
  parsePace,
  resolveOptionalReferenceSystem,
  resolveSportReferenceSystem,
} from '@shared/charts/chart-axis-units';
import {
  IPerformanceLimitsValue,
  PerformanceLimitsFieldKey,
} from '@shared/components/performance-limits-table/performance-limits-table';
import {
  IPerformanceLimitsDatapoint,
  IPerformanceLimitsDatapointChange,
  IPerformanceLimitsDatapointInvalidChange,
  PerformanceLimitsMetricComponent,
} from '@shared/components/performance-limits-metric/performance-limits-metric';

interface ICategoryOption {
  id: number;
  name: string;
  readonly: boolean;
}

interface IFieldConfig {
  inputKind: 'number' | 'pace';
  unitLabel: string;
  toDisplay: (base: number) => number;
  toBase: (display: number) => number;
  format: (value: number) => string;
}

interface IRowViewModel {
  key: string;
  record: ITrainingLevelLimit;
  fieldConfig: IFieldConfig;
}

interface IMetricGroup {
  key: string;
  metricName: string;
  datapoints: IRowViewModel[];
}

interface IMetricCard {
  key: string;
  metricName: string;
  datapoints: IPerformanceLimitsDatapoint[];
}

interface ISportContext {
  simulationType: number;
  primaryType: number;
  secondaryType: number;
}

const NUMERIC_FIELD_CONFIG: IFieldConfig = {
  inputKind: 'number',
  unitLabel: '',
  toDisplay: (base) => base,
  toBase: (display) => display,
  format: (value) => String(value),
};

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function resolveFieldConfig(units: string, ctx: ISportContext | null): IFieldConfig {
  if (!ctx) return NUMERIC_FIELD_CONFIG;

  const system =
    units === 'primary'
      ? resolveSportReferenceSystem(ctx.primaryType, ctx.simulationType)
      : units === 'secondary'
        ? resolveOptionalReferenceSystem(ctx.secondaryType)
        : null;

  if (system === null) {
    return { ...NUMERIC_FIELD_CONFIG, unitLabel: units === 'secondary' ? '' : units };
  }

  const def = AXIS_UNIT_DEFS[system];
  return {
    inputKind: def.inputKind,
    unitLabel: def.label,
    toDisplay: (base) => def.fromBase(base),
    toBase: (display) => def.toBase(display),
    format: def.format,
  };
}

/**
 * Master/detail sub-view for editing (or, for default categories, viewing) a training
 * level's per-sport datapoint limits. Values are stored server-side in SI base units
 * (m/s, watts) but displayed/edited in the sport's configured reference-system unit —
 * e.g. pace (m:ss.s) for a speed sport whose primary reference system is PACE_MIN_KM.
 */
@Component({
  selector: 'app-athlete-category-limits',
  templateUrl: 'category-limits.html',
  styleUrl: 'category-limits.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TrainingLevelLimitsStore],
  imports: [
    FormsModule,
    TuiAppearance,
    TuiCardMedium,
    TuiChevron,
    TuiDataListWrapper,
    TuiLabel,
    TuiLoader,
    TuiNotification,
    TuiSelect,
    TuiTextfield,
    PerformanceLimitsMetricComponent,
  ],
})
export class CategoryLimitsComponent {
  readonly initialLevelId = input<number | null>(null);

  private readonly store = inject(TrainingLevelLimitsStore);
  private readonly sportConfigStore = inject(SportConfigurationStore);
  private readonly trainingLevelStore = inject(TrainingLevelStore);
  private readonly toasts = inject(TuiToastService);

  protected readonly categoryOptions = computed<ICategoryOption[]>(() => [
    ...this.trainingLevelStore.defaultLevels().map((l) => ({ id: l.id, name: l.value, readonly: true })),
    ...this.trainingLevelStore.categories().map((c) => ({ id: c.id, name: c.name, readonly: false })),
  ]);

  /** Seeded from the input; recomputes when the input or options change, then user-controlled. */
  protected readonly selectedCategory = linkedSignal<ICategoryOption | null>(() => {
    const id = this.initialLevelId();
    return id === null ? null : (this.categoryOptions().find((o) => o.id === id) ?? null);
  });
  protected readonly stringifyCategory = (o: ICategoryOption): string => o.name;

  /** Read-only-ness derives from the picked category — default categories are view-only. */
  protected readonly readonly = computed(() => this.selectedCategory()?.readonly ?? false);
  protected readonly isLoadingCategories = this.trainingLevelStore.isLoading;

  protected readonly sports = this.sportConfigStore.sports;
  protected readonly isLoadingSports = this.sportConfigStore.isLoading;
  protected readonly isLoadingLimits = this.store.isLoading;
  protected readonly saving = signal(false);
  protected readonly isDirty = signal(false);

  /** Defaults to the first sport once the (prefetched) list loads; stays user-controlled after. */
  protected readonly selectedSport = linkedSignal<ISportOption | null>(
    () => this.sports()[0] ?? null,
  );
  protected readonly stringify = (option: ISportOption): string => option.name;

  /** Client-only metric filter — empty (`null`) means "show all". */
  protected readonly selectedMetric = signal<string | null>(null);

  private readonly sportContext = computed<ISportContext | null>(() => {
    const sport = this.selectedSport();
    if (!sport) return null;

    const config = this.sportConfigStore.configurationForSport(sport.id);
    if (!config) return null;

    return {
      simulationType: config.feature.sport.simulation_type,
      primaryType: config.data.primary_type,
      secondaryType: config.data.secondary_type,
    };
  });

  protected readonly rows = computed<IRowViewModel[]>(() => {
    const ctx = this.sportContext();
    return this.store
      .limits()
      .map((record) => ({
        key: `${record.metric_slug}:${record.datapoint_id}`,
        record,
        fieldConfig: resolveFieldConfig(record.units, ctx),
      }))
      .sort(
        (a, b) =>
          a.record.metric_slug.localeCompare(b.record.metric_slug) ||
          a.record.datapoint_id.localeCompare(b.record.datapoint_id),
      );
  });

  /** Rows grouped by metric — consecutive since {@link rows} is already metric_slug-sorted. */
  protected readonly metricGroups = computed<IMetricGroup[]>(() => {
    const groups: IMetricGroup[] = [];

    for (const row of this.rows()) {
      const current = groups.at(-1);
      if (current && current.key === row.record.metric_slug) {
        current.datapoints.push(row);
      } else {
        groups.push({ key: row.record.metric_slug, metricName: row.record.metric_name, datapoints: [row] });
      }
    }

    return groups;
  });

  protected readonly metricNames = computed(() =>
    this.metricGroups().map((group) => group.metricName),
  );

  /** {@link metricGroups}, narrowed to the selected metric — empty selection shows all. */
  protected readonly visibleGroups = computed<IMetricGroup[]>(() => {
    const metric = this.selectedMetric();
    return metric === null
      ? this.metricGroups()
      : this.metricGroups().filter((group) => group.metricName === metric);
  });

  protected readonly drafts = linkedSignal<Record<string, IPerformanceLimitsValue>>(() =>
    Object.fromEntries(this.rows().map((row) => [row.key, this.buildDraft(row)])),
  );

  /** {@link visibleGroups} reshaped into `app-performance-limits-metric` inputs. */
  protected readonly metricCards = computed<IMetricCard[]>(() => {
    const drafts = this.drafts();
    return this.visibleGroups().map((group) => ({
      key: group.key,
      metricName: group.metricName,
      datapoints: group.datapoints.map((row) => ({
        key: row.key,
        datapointId: row.record.datapoint_id,
        name: row.record.datapoint_name,
        unitLabel: row.fieldConfig.unitLabel,
        value: drafts[row.key],
      })),
    }));
  });

  /** Per-row validity, reported by each row's `app-performance-limits-table` instance. */
  private readonly invalidRows = linkedSignal<Record<string, boolean>>(() =>
    Object.fromEntries(this.rows().map((row) => [row.key, false])),
  );

  protected readonly hasErrors = computed(() => Object.values(this.invalidRows()).some(Boolean));

  /** Public — read by the parent page to enable/disable its "Save limits" header action. */
  readonly canSave = computed(
    () =>
      !this.readonly() &&
      this.isDirty() &&
      !this.hasErrors() &&
      !this.saving() &&
      this.rows().length > 0,
  );

  constructor() {
    effect(() => {
      const category = this.selectedCategory();
      if (category) this.store.setLevelId(category.id);
    });
    // Also fires for the linkedSignal's own default (first sport) — that's what lets the
    // limits fetch start as soon as the prefetched sport list resolves, no user click needed.
    effect(() => this.store.setSportId(this.selectedSport()?.id ?? null));
    // A fresh load (category/sport change) is the new baseline.
    effect(() => {
      this.rows();
      this.isDirty.set(false);
    });
  }

  protected onSportChange(sport: ISportOption | null): void {
    this.selectedSport.set(sport);
  }

  protected updateDraft({ key, value }: IPerformanceLimitsDatapointChange): void {
    this.drafts.update((drafts) => ({ ...drafts, [key]: value }));
    this.isDirty.set(true);
  }

  protected setRowInvalid({ key, invalid }: IPerformanceLimitsDatapointInvalidChange): void {
    this.invalidRows.update((rows) => ({ ...rows, [key]: invalid }));
  }

  /** Public — invoked by the parent page's "Save limits" header action. */
  save(): void {
    if (!this.canSave()) return;

    const drafts = this.drafts();
    const items = this.rows().map((row) => {
      const draft = drafts[row.key];
      const toBase = (field: PerformanceLimitsFieldKey): number => {
        const display = draft.kind === 'pace' ? parsePace(draft[field]) : draft[field];
        return row.fieldConfig.toBase(display ?? 0);
      };

      return {
        ...row.record,
        male_upper: toBase('male_upper'),
        male_lower: toBase('male_lower'),
        male_stdDev: toBase('male_stdDev'),
        female_upper: toBase('female_upper'),
        female_lower: toBase('female_lower'),
        female_stdDev: toBase('female_stdDev'),
      };
    });

    this.saving.set(true);
    this.store.save(items).subscribe({
      next: () => {
        this.toasts.open('Limits updated.', { appearance: 'positive' }).subscribe();
        this.saving.set(false);
      },
      error: () => {
        this.toasts.open('Failed to update limits.', { appearance: 'negative' }).subscribe();
        this.saving.set(false);
      },
    });
  }

  private buildDraft(row: IRowViewModel): IPerformanceLimitsValue {
    const { fieldConfig, record } = row;

    if (fieldConfig.inputKind === 'pace') {
      return {
        kind: 'pace',
        male_upper: fieldConfig.format(fieldConfig.toDisplay(record.male_upper)),
        male_lower: fieldConfig.format(fieldConfig.toDisplay(record.male_lower)),
        male_stdDev: fieldConfig.format(fieldConfig.toDisplay(record.male_stdDev)),
        female_upper: fieldConfig.format(fieldConfig.toDisplay(record.female_upper)),
        female_lower: fieldConfig.format(fieldConfig.toDisplay(record.female_lower)),
        female_stdDev: fieldConfig.format(fieldConfig.toDisplay(record.female_stdDev)),
      };
    }

    return {
      kind: 'number',
      male_upper: round2(fieldConfig.toDisplay(record.male_upper)),
      male_lower: round2(fieldConfig.toDisplay(record.male_lower)),
      male_stdDev: round2(fieldConfig.toDisplay(record.male_stdDev)),
      female_upper: round2(fieldConfig.toDisplay(record.female_upper)),
      female_lower: round2(fieldConfig.toDisplay(record.female_lower)),
      female_stdDev: round2(fieldConfig.toDisplay(record.female_stdDev)),
    };
  }
}
